// src/components/Group.js

import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db, auth } from "../api/firebase";

import {
  Box,
  Stack,
  Typography,
  Drawer,
  useMediaQuery,
  CircularProgress,
  Chip,
} from "@mui/material";

import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";

import GroupMembers from "./GroupMembers";
import GroupChatEx from "./GroupChatEx";
import Webinar from "./Webinar";
import SophieMission2025HL from "./SophieMission2025HL";
import SurveyDialog from "./SurveyDialog";
import BottomNavBar2025HL from "./BottomNavBar2025HL";
import GroupYaeChikaiPage from "./GroupYaeChikaiPage";

import { AppThemeContext } from "./AppShell";
import BottomNavBar2025HLN from "./BottomNavBar2025HLN";

const Group2025LHN = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const muiSmall = useMediaQuery("(max-width:600px)");

  const theme = useContext(AppThemeContext) || {};
  const surface = theme.surfaceBg || "#FFFFFF";
  const appBg = theme.appBg || "#F5F5F9";
  const accent = theme.railActive || "#5A35FF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [value, setValue] = useState(0);
  const [currentView, setCurrentView] = useState("仲間");

  const [user, setUser] = useState(null);
  const [isMissionDrawerOpen, setMissionDrawerOpen] = useState(false);
  const [isSurveyDialogOpen, setSurveyDialogOpen] = useState(false);

  const surveyId = "202505Early";

  // ★ 数値(px) と 文字列(px) を分けて持つ
  const topOffsetPx = muiSmall ? 56 : 64;
  const topOffsetCss = `${topOffsetPx}px`;

  // ---------------- ユーザー・FAB ----------------
  useEffect(() => {
    const checkSurveyResponse = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) return;

        const userDocRef = doc(db, "users", userId);
        const userDocSnap = await getDoc(userDocRef);
        if (!userDocSnap.exists()) {
          console.error("User not found");
          return;
        }

        const userData = userDocSnap.data();
        setUser(userData);

        // FAB の有無は今は UI に出してないので、取得だけ
        const userSurveyQuery = query(
          collection(db, "users", userId, "fab"),
          where("surveyId", "==", surveyId)
        );
        await getDocs(userSurveyQuery);
      } catch (error) {
        console.error("Failed to fetch user", error);
      }
    };

    checkSurveyResponse();
  }, [surveyId]);

  // ---------------- グループ情報 ----------------
  useEffect(() => {
    const fetchGroup = async () => {
      if (!user) return;

      setLoading(true);
      try {
        if (!user.isAdmin && user.groups2025SecondHalf !== id) {
          if (user.groups2025SecondHalf) {
            navigate(`/groups2025hl/${user.groups2025SecondHalf}`, {
              replace: true,
            });
          }
          return;
        }

        const docRef = doc(db, "groups2025halfSecond", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setGroup({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError("グループが見つかりませんでした。");
        }
      } catch (err) {
        console.error(err);
        setError("グループ情報の取得に失敗しました。");
      } finally {
        setLoading(false);
      }
    };

    fetchGroup();
  }, [id, user, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [value]);

  const handleMissionClick = () => setMissionDrawerOpen(true);
  const handleDrawerClose = () => setMissionDrawerOpen(false);

  const handleNavigationChange = (event, newValue) => {
    setValue(newValue);
    if (newValue === 0) setCurrentView("仲間");
    else if (newValue === 1) setCurrentView("グループチャット");
    else if (newValue === 2) setCurrentView("八重の誓");
    else if (newValue === 3) setCurrentView("12/7");
  };

  const handleSurveyClose = () => setSurveyDialogOpen(false);

  if (loading || !user) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: appBg,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" sx={{ color: textBase, opacity: 0.7 }}>
            グループ情報を読み込んでいます…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: appBg,
        }}
      >
        <Typography variant="body2" sx={{ color: textBase }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        pb: { xs: 7, md: 4 },
        background: appBg,
      }}
    >
      {/* ★ コンテンツ側は数値(px)で余白を取る */}
      <Box
        sx={{
          //   mt: topOffsetPx + 8, // 少しだけ余裕を追加
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 3, sm: 4 },
        }}
      >
        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            borderRadius: { xs: 3, sm: 4 },
            bgcolor: surface,
            boxShadow: "0 20px 60px rgba(20,16,40,0.10)",
            border: `1px solid ${border}`,
            overflow: "hidden",
          }}
        >
          {/* ヘッダー */}
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              borderBottom: `1px solid ${border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  bgcolor: `${accent}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GroupsRoundedIcon sx={{ color: accent, fontSize: 22 }} />
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    fontSize: 11,
                    opacity: 0.7,
                    color: textBase,
                  }}
                >
                  MASTER MIND GROUP
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.03em",
                    color: textBase,
                  }}
                >
                  {group?.name || `グループ ${group?.groupNumber || ""}`}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              {group?.groupNumber && (
                <Chip
                  label={`第${group.groupNumber}グループ`}
                  size="small"
                  sx={{
                    bgcolor: `${accent}10`,
                    color: accent,
                    fontWeight: 600,
                  }}
                />
              )}
              {user?.isAdmin && (
                <Chip
                  label="管理者モード"
                  size="small"
                  sx={{
                    bgcolor: "rgba(0,0,0,0.06)",
                    color: "rgba(0,0,0,0.7)",
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>
          </Box>
          {/* ★ ここには CSS 用に "56px" / "64px" を渡す */}
          {/* ヘッダーのすぐ下 */}
          <Box sx={{ borderBottom: `1px solid ${border}` }}>
            <BottomNavBar2025HLN
              value={value}
              onChange={handleNavigationChange}
              handleMissionClick={handleMissionClick}
              user={user}
              
            />
          </Box>
          {/* 本文 */}
          <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
            {currentView === "仲間" && <GroupMembers group={group} />}
            {currentView === "グループチャット" && (
              <Box sx={{ minHeight: 320 }}>
                <GroupChatEx groupId={id} />
              </Box>
            )}
            {currentView === "八重の誓" && (
              <Box sx={{ minHeight: 320 }}>
                <GroupYaeChikaiPage />
              </Box>
            )}
            {currentView === "12/7" && (
              <Box sx={{ minHeight: 320 }}>
                <Webinar />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={isMissionDrawerOpen}
        onClose={handleDrawerClose}
      >
        <Box sx={{ width: { xs: "90vw", sm: 420 }, maxWidth: "100vw" }}>
          <SophieMission2025HL onClose={handleDrawerClose} />
        </Box>
      </Drawer>

      <SurveyDialog
        open={isSurveyDialogOpen}
        onClose={handleSurveyClose}
        surveyId={surveyId}
        groupId={id}
        userId={auth.currentUser?.uid}
      />
    </Box>
  );
};

export default Group2025LHN;
